// @flow
import saveResponse from 'api/response'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import { ResourceStripeContext } from 'components/ResourceFill/providers/ResourceStripeProvider'
import type { FormContext } from 'components/ResourceFill/types/form-context'
import useAnswerCellState from 'hooks/answers/useAnswerCellState'
import some from 'lodash/some'
import * as React from 'react'
import type { Question } from 'types/question'
import {
  applyAnswerToResponse,
  getQuestionError,
  isQuestionValid,
  removeErrorId,
  responseHasValidAnswers,
} from 'utils/answers'
import { saveResponseErrorHelper } from 'utils/answerStateHelper'
import { visibleByConditionalRulesHelper } from 'utils/formHelpers'
import { arrayUpdateByKey, hasText, isFilledArray } from 'utils/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

type Props = {
  children: React.Node,
}

export const ResourceFormContext: React.Context<?FormContext> =
  React.createContext(null)

function ResourceFormProvider({ children }: Props): React.Node {
  const { mode, source } = React.useContext(ResourceSharedContext) || {}

  const { response, setResponse } =
    React.useContext(ResourceResponseContext) || {}

  const { currentSession, setCurrentSession } =
    React.useContext(ResourceSessionsContext) || {}

  const { answerErrors, setAnswerErrors } =
    React.useContext(ZentakeContext) || {}

  const { stripeEmpty, submitStripeData, handleCreditCardAnswerChange } =
    React.useContext(ResourceStripeContext) || {}

  const [updatedQuestions, setUpdatedQuestions] = React.useState([])
  const [updatedSignatureQuestions, setUpdatedSignatureQuestions] =
    React.useState([])
  const [allDirty, setAllDirty] = React.useState(false)
  const [questionIdToScroll, setQuestionIdToScroll] = React.useState(null)

  const [updatedCellQuestions, onAnswerCellChanged] = useAnswerCellState({
    isOfficeUseForm: mode === 'office',
    response,
    setResponse,
    setAllDirty,
    setAnswerErrors,
    source,
  })

  React.useEffect(
    () => setAnswerErrors({}),
    // eslint-disable-next-line
    []
  )

  const validateQuestions = React.useCallback(
    () =>
      !responseHasValidAnswers(
        response,
        mode,
        setQuestionIdToScroll,
        setAnswerErrors,
        stripeEmpty
      ),
    [mode, response, setAnswerErrors, stripeEmpty]
  )

  const filterHiddenAnswers = React.useCallback((response) => {
    const questions = []

    response.form?.pages.forEach((page) => {
      page.sections.forEach((section) => {
        if (section.rows) {
          section.rows.forEach((row) => {
            questions.push(...row.questions)
          })
        }

        if (section.questions) {
          questions.push(...section.questions)
        }
      })
    })

    const questionsHiddenByConditionalLogic = questions
      .filter(
        (question) =>
          !visibleByConditionalRulesHelper(
            question,
            response.answers,
            questions
          )
      )
      .map((question) => question.id)

    const filteredAnswers = []
    const answersToHiddenQuestions = []

    response?.answers?.forEach((answer) => {
      if (questionsHiddenByConditionalLogic.includes(answer.question)) {
        answersToHiddenQuestions.push(answer)
        return
      }
      filteredAnswers.push(answer)
    })

    return {
      ...response,
      answers: filteredAnswers,
      answersToHiddenQuestions,
      save_answer_repeat_section: response.save_answer_repeat_section || [],
    }
  }, [])

  const submitResponse = React.useCallback(
    async (submit = false, lockInternalForm = false) => {
      if (!submitStripeData) return
      if (!handleCreditCardAnswerChange) return
      if (!setResponse) return

      const paymentMethodId = await submitStripeData()

      const [updatedResponse, questionId] =
        handleCreditCardAnswerChange(paymentMethodId)

      setResponse(updatedResponse)

      const newUpdatedQuestions = questionId
        ? [...updatedQuestions, questionId]
        : updatedQuestions

      setUpdatedQuestions(newUpdatedQuestions)

      const extraResponses =
        source === 'tablet' && !currentSession?.packetResponseId
          ? currentSession?.responses.map((response) => response.id) || []
          : []

      await saveResponse({
        extraResponses,
        mode: source || '',
        response: filterHiddenAnswers(updatedResponse),
        submit,
        lockInternalForm,
        updatedCellQuestions,
        updatedQuestions: newUpdatedQuestions,
        updatedSignatureQuestions,
        save_answer_repeat_section:
          updatedResponse?.save_answer_repeat_section || [],
      })
    },
    [
      submitStripeData,
      handleCreditCardAnswerChange,
      setResponse,
      updatedQuestions,
      source,
      currentSession?.packetResponseId,
      currentSession?.responses,
      filterHiddenAnswers,
      updatedCellQuestions,
      updatedSignatureQuestions,
    ]
  )

  const updateSession = React.useCallback(
    async (submit, lockInternalForm) => {
      if (!currentSession) return
      if (!setCurrentSession) return

      try {
        await submitResponse(submit, lockInternalForm)

        const newCurrentSession = {
          ...currentSession,
          responses: arrayUpdateByKey(
            currentSession?.responses,
            'id',
            response?.id,
            response
          ),
        }
        setCurrentSession(newCurrentSession)
        return newCurrentSession
      } catch (error) {
        saveResponseErrorHelper(
          error?.answerErrors,
          setAnswerErrors,
          setQuestionIdToScroll
        )
        throw error
      }
    },
    [
      currentSession,
      setCurrentSession,
      submitResponse,
      response,
      setAnswerErrors,
    ]
  )

  const saveForm = React.useCallback(
    async (validate, submit, lockInternalForm) => {
      if (!validateQuestions) return
      if (!setAllDirty) return

      try {
        if (validate && validateQuestions()) {
          setAllDirty(true)
          return
        }

        setAllDirty(false)

        return await updateSession(submit, lockInternalForm)
      } catch (error) {
        if (error?.answerErrors) {
          setAnswerErrors(error.answerErrors)
          const errorIdToScroll = Object.keys(error.answerErrors)[0]
          errorIdToScroll && setQuestionIdToScroll(errorIdToScroll)
        }
      }
    },
    [setAnswerErrors, updateSession, validateQuestions]
  )

  const getResponseQuestionError = React.useCallback(
    (question: Question) => {
      if (
        (!question.is_office_use_only && mode !== 'office') ||
        (question.is_office_use_only && mode === 'office') ||
        mode === 'internal'
      ) {
        return getQuestionError(answerErrors, question)
      }
    },
    [answerErrors, mode]
  )

  const isResponseQuestionValid = React.useCallback(
    (question: Question) => {
      if (
        question.is_office_use_only &&
        !['office', 'internal'].includes(mode)
      ) {
        return true
      }

      if (!question.is_office_use_only && mode === 'office') {
        return true
      }

      const questionErrorMessage = getQuestionError(answerErrors, question)

      if (hasText(questionErrorMessage)) {
        return true
      }

      return isQuestionValid(response, question, setAnswerErrors)
    },
    [answerErrors, mode, response, setAnswerErrors]
  )

  const isQuestionDirty = React.useCallback(
    (question: Question) => {
      if (!updatedSignatureQuestions) return
      if (!updatedCellQuestions) return
      if (!updatedQuestions) return

      if (allDirty) {
        return true
      }

      // $FlowFixMe
      if (question.question_type === 'signature') {
        return (
          updatedSignatureQuestions.findIndex((id) => id === question.id) >= 0
        )
      }

      // $FlowFixMe
      if (question.question_type === 'matrix') {
        return question.question_rows.every((r) =>
          updatedCellQuestions.some((c) => c.rowId === r)
        )
      }

      return updatedQuestions.findIndex((id) => id === question.id) >= 0
    },
    [
      allDirty,
      updatedCellQuestions,
      updatedQuestions,
      updatedSignatureQuestions,
    ]
  )

  const isInternal = response?.message?.message_type === 'internal'

  const onAnswerChanged = React.useCallback(
    (question, answerContext) => {
      if (question.is_office_use_only && mode !== 'office' && !isInternal) {
        return
      }

      if (!setResponse) return
      if (!setUpdatedQuestions) return

      setResponse((response) => {
        if (!response) return response

        return applyAnswerToResponse(response, question, answerContext)
      })

      removeErrorId(question, setAnswerErrors)

      setUpdatedQuestions((prev) => {
        const elementInUpdatedList = prev?.find((id) => id === question.id)
        if (elementInUpdatedList === undefined) {
          return [...prev, question.id]
        }
        return prev
      })
    },
    [isInternal, mode, setAnswerErrors, setResponse, setUpdatedQuestions]
  )

  const onAnswerSignatureChanged = React.useCallback(
    (question, imageAndText) => {
      if (!setResponse) return
      if (!setAllDirty) return
      if (!setUpdatedSignatureQuestions) return
      if (question.is_office_use_only && mode !== 'office') return

      const newResponse = applyAnswerToResponse(
        response,
        question,
        imageAndText
      )
      setResponse(newResponse)
      setAllDirty(false)
      removeErrorId(question, setAnswerErrors)

      setUpdatedSignatureQuestions((prev) => {
        if (!isFilledArray(prev)) return [question.id]
        const hasElement = some(prev, (item) => item === question.id)
        return hasElement ? prev : [...prev, question.id]
      })
    },
    [
      mode,
      response,
      setAllDirty,
      setAnswerErrors,
      setResponse,
      setUpdatedSignatureQuestions,
    ]
  )

  const value = React.useMemo(() => {
    return {
      allDirty,
      validateQuestions,
      setAllDirty,
      saveForm,
      updatedSignatureQuestions,
      updatedQuestions,
      setUpdatedQuestions,
      updatedCellQuestions,
      setUpdatedSignatureQuestions,
      questionIdToScroll,
      setQuestionIdToScroll,
      onAnswerCellChanged,
      getResponseQuestionError,
      isResponseQuestionValid,
      isQuestionDirty,
      onAnswerChanged,
      onAnswerSignatureChanged,
      templateId: currentSession?.templateId,
    }
  }, [
    allDirty,
    getResponseQuestionError,
    isQuestionDirty,
    isResponseQuestionValid,
    onAnswerCellChanged,
    onAnswerChanged,
    onAnswerSignatureChanged,
    questionIdToScroll,
    saveForm,
    updatedCellQuestions,
    updatedQuestions,
    updatedSignatureQuestions,
    validateQuestions,
    currentSession?.templateId,
  ])

  return (
    <ResourceFormContext.Provider value={value}>
      {children}
    </ResourceFormContext.Provider>
  )
}

export default ResourceFormProvider
